/**
 * ResetPasswordModal — Leadership-only password reset dialog.
 *
 * Two methods:
 *   - "Generate Temporary Password" → sets a non-permanent password server-side and displays it in
 *     a copyable code block (the only time it is shown).
 *   - "Send Reset Email" → triggers the Cognito forgot-password email.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Check, Copy, Loader2 } from 'lucide-react';

import { useResetPassword } from '@/hooks/useUsers';
import type { DeliverProUser, ResetPasswordMethod } from '@/types';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export function ResetPasswordModal({
  user,
  open,
  onOpenChange,
}: {
  user: DeliverProUser;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const resetPassword = useResetPassword();

  const [method, setMethod] = useState<ResetPasswordMethod>('temporary');
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Reset transient state each time the dialog is opened/closed.
  useEffect(() => {
    if (!open) {
      setMethod('temporary');
      setTempPassword(null);
      setCopied(false);
    }
  }, [open]);

  const handleSubmit = (): void => {
    resetPassword.mutate(
      { userId: user.id, method },
      {
        onSuccess: (result) => {
          if ('temp_password' in result) {
            setTempPassword(result.temp_password);
            toast.success('Temporary password generated');
          } else {
            toast.success(`Reset email sent to ${user.email}`);
            onOpenChange(false);
          }
        },
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : 'Failed to reset password');
        },
      }
    );
  };

  const handleCopy = async (): Promise<void> => {
    if (!tempPassword) return;
    try {
      await navigator.clipboard.writeText(tempPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reset password</DialogTitle>
          <DialogDescription>Reset the password for {user.full_name}.</DialogDescription>
        </DialogHeader>

        {tempPassword ? (
          <div className="space-y-2 py-1">
            <Label>Temporary password</Label>
            <div className="flex items-center gap-2">
              <code className="flex-1 select-all break-all rounded-md border bg-subtle-bg px-3 py-2 font-mono text-sm text-text-primary">
                {tempPassword}
              </code>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={handleCopy}
                aria-label="Copy temporary password"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-xs text-text-secondary">
              Share this securely — it will not be shown again. The user must change it on next
              sign-in.
            </p>
          </div>
        ) : (
          <fieldset className="space-y-3 py-1">
            <legend className="sr-only">Reset method</legend>
            <label className="flex cursor-pointer items-start gap-3 rounded-md border p-3 hover:bg-primary-subtle">
              <input
                type="radio"
                name="reset-method"
                value="temporary"
                checked={method === 'temporary'}
                onChange={() => setMethod('temporary')}
                className="mt-0.5"
              />
              <span>
                <span className="block text-sm font-medium text-text-primary">
                  Generate Temporary Password
                </span>
                <span className="block text-xs text-text-secondary">
                  Create a temporary password to share manually.
                </span>
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-3 rounded-md border p-3 hover:bg-primary-subtle">
              <input
                type="radio"
                name="reset-method"
                value="link"
                checked={method === 'link'}
                onChange={() => setMethod('link')}
                className="mt-0.5"
              />
              <span>
                <span className="block text-sm font-medium text-text-primary">Send Reset Email</span>
                <span className="block text-xs text-text-secondary">
                  Email the user a password reset link.
                </span>
              </span>
            </label>
          </fieldset>
        )}

        <DialogFooter>
          {tempPassword ? (
            <Button type="button" onClick={() => onOpenChange(false)}>
              Done
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={resetPassword.isPending}
              >
                Cancel
              </Button>
              <Button type="button" onClick={handleSubmit} disabled={resetPassword.isPending}>
                {resetPassword.isPending && <Loader2 className="animate-spin" aria-hidden="true" />}
                {method === 'temporary' ? 'Generate' : 'Send email'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ResetPasswordModal;
